import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildingFootprintCells,
  buildingShortLabel,
} from './sc2/replayUnitLookup';
import {
  cameraAtTime,
  cameraCellGridLines,
  cameraWorldWindow,
  colorForPlayer,
  decodeCliffB64,
  entitiesAtTime,
  nextCameraZoom,
  worldToCameraCanvas,
} from '../replayPlayerState';
import './CameraStage.css';

const RESOURCE_COLORS = {
  mineral: '#4fc3f7',
  gas: '#66bb6a',
};
const DRAW_ORDER = { mineral: 0, gas: 1, building: 2, worker: 3, army: 4 };

function drawDiamond(ctx, px, py, r) {
  ctx.beginPath();
  ctx.moveTo(px, py - r);
  ctx.lineTo(px + r, py);
  ctx.lineTo(px, py + r);
  ctx.lineTo(px - r, py);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function cliffFill(level, min, max) {
  if (max <= min) return 'rgb(28, 42, 58)';
  const t = Math.min(1, Math.max(0, (level - min) / (max - min)));
  const r = Math.round(22 + t * 48);
  const g = Math.round(34 + t * 40);
  const b = Math.round(48 + t * 28);
  return `rgb(${r},${g},${b})`;
}

function buildingCanvasRect(entity, window, width, height) {
  const fp = buildingFootprintCells(entity.name);
  const half = fp / 2;
  const tl = worldToCameraCanvas(
    entity.x - half,
    entity.y + half,
    window,
    width,
    height
  );
  const br = worldToCameraCanvas(
    entity.x + half,
    entity.y - half,
    window,
    width,
    height
  );
  return {
    x: tl.x,
    y: tl.y,
    w: br.x - tl.x,
    h: br.y - tl.y,
    fp,
  };
}

/**
 * Replay Player Camera — zoomed axis-aligned window around the selected
 * observer's camera sample (D61), with cliff underlay (D60), cell grid (D66),
 * footprints (D67), labels (D68), and zoom (D69).
 */
function CameraStage({
  minimap,
  cameras = null,
  cameraPid = null,
  time = 0,
  apiBaseUrl = null,
  mapFileHash = null,
  width = 640,
  height = 360,
  onHoverChange,
}) {
  const canvasRef = useRef(null);
  const hitRef = useRef([]);
  const onHoverRef = useRef(onHoverChange);
  onHoverRef.current = onHoverChange;
  const [terrain, setTerrain] = useState({ status: 'idle', grid: null });
  const [zoom, setZoom] = useState(1);

  const playersByPid = useMemo(() => {
    const map = new Map();
    (minimap?.players || []).forEach((p) => map.set(p.pid, p));
    return map;
  }, [minimap]);

  const camera = useMemo(
    () => cameraAtTime(cameras, cameraPid, time),
    [cameras, cameraPid, time]
  );

  const entities = useMemo(
    () => entitiesAtTime(minimap?.events || [], time),
    [minimap, time]
  );

  useEffect(() => {
    if (!apiBaseUrl || !mapFileHash) {
      setTerrain({ status: 'empty', grid: null });
      return undefined;
    }
    let cancelled = false;
    setTerrain({ status: 'loading', grid: null });
    fetch(`${apiBaseUrl}/map_geometry/${encodeURIComponent(mapFileHash)}`)
      .then(async (response) => {
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setTerrain({ status: 'empty', grid: null });
          return;
        }
        const cliff = decodeCliffB64(data.cliff_b64, data.width, data.height);
        if (!cliff) {
          setTerrain({ status: 'empty', grid: null });
          return;
        }
        setTerrain({
          status: 'ready',
          grid: {
            width: data.width,
            height: data.height,
            cliff,
          },
        });
      })
      .catch(() => {
        if (!cancelled) setTerrain({ status: 'empty', grid: null });
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, mapFileHash]);

  const mapW = minimap?.width || 0;
  const mapH = minimap?.height || 0;

  const viewWindow = useMemo(() => {
    if (!camera || mapW <= 0 || mapH <= 0) return null;
    return cameraWorldWindow({
      mapW,
      mapH,
      x: camera.x,
      y: camera.y,
      zoom,
    });
  }, [camera, mapH, mapW, zoom]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !minimap || !camera || !viewWindow || mapW <= 0 || mapH <= 0) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const window = viewWindow;
    const worldW = window.right - window.left;
    const worldH = window.top - window.bottom;
    const cellPx = Math.min(width / worldW, height / worldH);
    const hits = [];

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#0f1620';
    ctx.fillRect(0, 0, width, height);

    const grid = terrain.status === 'ready' ? terrain.grid : null;
    if (grid) {
      let minC = Infinity;
      let maxC = -Infinity;
      const x0 = Math.max(0, Math.floor(window.left));
      const x1 = Math.min(grid.width - 1, Math.ceil(window.right));
      const y0 = Math.max(0, Math.floor(window.bottom));
      const y1 = Math.min(grid.height - 1, Math.ceil(window.top));
      for (let gy = y0; gy <= y1; gy += 1) {
        for (let gx = x0; gx <= x1; gx += 1) {
          const v = grid.cliff[gy * grid.width + gx];
          if (v < minC) minC = v;
          if (v > maxC) maxC = v;
        }
      }
      for (let gy = y0; gy <= y1; gy += 1) {
        for (let gx = x0; gx <= x1; gx += 1) {
          const v = grid.cliff[gy * grid.width + gx];
          const tl = worldToCameraCanvas(gx, gy + 1, window, width, height);
          const br = worldToCameraCanvas(gx + 1, gy, window, width, height);
          ctx.fillStyle = cliffFill(v, minC, maxC);
          ctx.fillRect(tl.x, tl.y, Math.ceil(br.x - tl.x) + 1, Math.ceil(br.y - tl.y) + 1);
        }
      }
    }

    const lines = cameraCellGridLines(window, width, height);
    ctx.strokeStyle = 'rgba(255,255,255,0.14)';
    ctx.lineWidth = 1;
    for (const x of lines.vertical) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (const y of lines.horizontal) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    const ordered = [...entities].sort(
      (a, b) => (DRAW_ORDER[a.kind] ?? 9) - (DRAW_ORDER[b.kind] ?? 9)
    );
    for (const entity of ordered) {
      if (entity.x == null || entity.y == null) continue;
      const pad = entity.kind === 'building' ? buildingFootprintCells(entity.name) / 2 : 1;
      if (
        entity.x < window.left - pad ||
        entity.x > window.right + pad ||
        entity.y < window.bottom - pad ||
        entity.y > window.top + pad
      ) {
        continue;
      }
      ctx.lineWidth = 1;

      if (entity.kind === 'mineral' || entity.kind === 'gas') {
        const { x: px, y: py } = worldToCameraCanvas(
          entity.x,
          entity.y,
          window,
          width,
          height
        );
        const r = Math.max(2.5, cellPx * 0.35);
        ctx.fillStyle = RESOURCE_COLORS[entity.kind];
        ctx.strokeStyle = 'rgba(0,0,0,0.45)';
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        continue;
      }

      const color = colorForPlayer(playersByPid.get(entity.pid));
      ctx.fillStyle = color;
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';

      if (entity.kind === 'building') {
        const rect = buildingCanvasRect(entity, window, width, height);
        ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
        ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
        const label = buildingShortLabel(entity.name);
        const fontPx = Math.max(9, Math.min(16, rect.w * 0.45));
        ctx.font = `bold ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(0,0,0,0.75)';
        ctx.strokeText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
        ctx.fillStyle = '#f4f7fb';
        ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
        hits.push({
          ...rect,
          name: entity.name || 'Building',
          kind: 'building',
          id: entity.id,
        });
      } else if (entity.kind === 'worker') {
        const { x: px, y: py } = worldToCameraCanvas(
          entity.x,
          entity.y,
          window,
          width,
          height
        );
        const r = Math.max(2.2, cellPx * 0.4);
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else {
        const { x: px, y: py } = worldToCameraCanvas(
          entity.x,
          entity.y,
          window,
          width,
          height
        );
        const r = Math.max(3, cellPx * 0.55);
        drawDiamond(ctx, px, py, r);
      }
    }
    hitRef.current = hits;
  }, [
    camera,
    entities,
    height,
    mapH,
    mapW,
    minimap,
    playersByPid,
    terrain,
    viewWindow,
    width,
  ]);

  useEffect(() => {
    draw();
  }, [draw]);

  const pickBuilding = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const sx = (clientX - rect.left) * (width / rect.width);
    const sy = (clientY - rect.top) * (height / rect.height);
    // Topmost last drawn wins — scan reverse.
    for (let i = hitRef.current.length - 1; i >= 0; i -= 1) {
      const h = hitRef.current[i];
      if (sx >= h.x && sx <= h.x + h.w && sy >= h.y && sy <= h.y + h.h) {
        return h;
      }
    }
    return null;
  };

  const emitHover = (hit, clientX, clientY) => {
    if (!onHoverRef.current) return;
    if (!hit) {
      onHoverRef.current(null);
      return;
    }
    onHoverRef.current({
      kind: 'camera',
      text: hit.name,
      clientX,
      clientY,
    });
  };

  const onPointerMove = (e) => {
    emitHover(pickBuilding(e.clientX, e.clientY), e.clientX, e.clientY);
  };

  const onPointerLeave = () => {
    onHoverRef.current?.(null);
  };

  const onClick = (e) => {
    const hit = pickBuilding(e.clientX, e.clientY);
    emitHover(hit, e.clientX, e.clientY);
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1 : -1;
    setZoom((z) => nextCameraZoom(z, delta));
  };

  if (!minimap || !(minimap.events || []).length) {
    return (
      <div className="camera-stage camera-stage--empty" data-testid="camera-stage">
        <div className="camera-stage-title">Camera</div>
        <p className="camera-stage-msg">No minimap data for Camera.</p>
      </div>
    );
  }

  if (!camera || camera.x == null || camera.y == null) {
    return (
      <div className="camera-stage camera-stage--empty" data-testid="camera-stage">
        <div className="camera-stage-title">Camera</div>
        <p className="camera-stage-msg">
          No camera sample for the selected player at this Playhead.
        </p>
      </div>
    );
  }

  return (
    <div className="camera-stage" data-testid="camera-stage">
      <div className="camera-stage-toolbar">
        <div className="camera-stage-title">Camera</div>
        <div className="camera-stage-zoom" role="group" aria-label="Camera zoom">
          <button
            type="button"
            className="camera-stage-zoom-btn"
            data-testid="camera-zoom-out"
            onClick={() => setZoom((z) => nextCameraZoom(z, -1))}
          >
            −
          </button>
          <span className="camera-stage-zoom-label" data-testid="camera-zoom-label">
            {zoom}×
          </span>
          <button
            type="button"
            className="camera-stage-zoom-btn"
            data-testid="camera-zoom-in"
            onClick={() => setZoom((z) => nextCameraZoom(z, 1))}
          >
            +
          </button>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        className="camera-stage-canvas"
        width={width}
        height={height}
        aria-label="Replay Camera"
        data-testid="camera-canvas"
        onMouseMove={onPointerMove}
        onMouseLeave={onPointerLeave}
        onClick={onClick}
        onWheel={onWheel}
      />
    </div>
  );
}

export default CameraStage;
